import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { INSURERS } from '../lib/constants';

const MAX_PHOTOS = 8;
const MAX_MB = 10;

export default function IntakePage({ token }) {
  const [shop, setShop] = useState(null); // { room_id, shop_name, logo_url } | null
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({
    client_name: '', client_phone: '', client_email: '',
    car: '', plate: '', color: '', vin: '',
    ins: '', cnum: '', description: '',
  });
  const [photos, setPhotos] = useState([]);
  const [photoNote, setPhotoNote] = useState('');
  const [hp, setHp] = useState(''); // honeypot — real users never see the field
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const loadedAt = useRef(new Date().toISOString());
  const scrollRef = useRef(null);

  useEffect(() => {
    document.title = 'New Claim Request';
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('get_intake_room', { token });
      if (!cancelled) {
        setShop(data && data[0] ? data[0] : null);
        setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function addPhotos(files) {
    const incoming = Array.from(files || []);
    const kept = incoming.filter(f => f.size <= MAX_MB * 1024 * 1024);
    const tooBig = incoming.length - kept.length;
    let next = [...photos, ...kept];
    let note = tooBig > 0 ? `${tooBig} photo${tooBig > 1 ? 's were' : ' was'} over ${MAX_MB} MB and skipped.` : '';
    if (next.length > MAX_PHOTOS) {
      next = next.slice(0, MAX_PHOTOS);
      note = `${note} Maximum ${MAX_PHOTOS} photos.`.trim();
    }
    setPhotos(next);
    setPhotoNote(note);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!form.client_name.trim() || !form.client_phone.trim() || !form.car.trim() || !form.ins.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const requestId = crypto.randomUUID();
      const paths = [];
      for (const file of photos) {
        const safeName = file.name.replace(/[^\w.-]/g, '_');
        const path = `intake/${shop.room_id}/${requestId}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage.from('claim-files').upload(path, file);
        if (!upErr) paths.push(path);
      }
      // NO .select() here — anon has INSERT-only on intake_requests
      const { error: insErr } = await supabase.from('intake_requests').insert({
        id: requestId,
        room_id: shop.room_id,
        client_name: form.client_name.trim(),
        client_phone: form.client_phone.trim(),
        client_email: form.client_email.trim() || null,
        car: form.car.trim(),
        plate: form.plate.trim().toUpperCase() || null,
        color: form.color.trim() || null,
        vin: form.vin.trim().toUpperCase() || null,
        ins: form.ins.trim() || null,
        cnum: form.cnum.trim() || null,
        description: form.description.trim(),
        photo_paths: paths,
        honeypot: hp,
        form_loaded_at: loadedAt.current,
      });
      if (insErr) throw insErr;
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      setDone(true);
    } catch {
      setError('Something went wrong sending your request. Please try again.');
    }
    setSubmitting(false);
  }

  if (checking) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );

  if (!shop) return (
    <Shell scrollRef={scrollRef}>
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>This link isn't valid</div>
        <div style={{ fontSize: 14, color: 'var(--text3)', marginTop: 10, lineHeight: 1.6 }}>
          Double-check the link you received from the shop, or contact them directly.
        </div>
      </div>
    </Shell>
  );

  if (done) return (
    <Shell scrollRef={scrollRef}>
      <div style={{ textAlign: 'center', padding: '70px 24px' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--orange-dim)', border: '2px solid var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>Request received</div>
        <div style={{ fontSize: 14.5, color: 'var(--text2)', marginTop: 10, lineHeight: 1.6, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
          Thanks — {shop.shop_name} will review your request and reach out to confirm details.
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 16 }}>You can close this page.</div>
      </div>
    </Shell>
  );

  const pair = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };

  return (
    <Shell scrollRef={scrollRef} header={
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {shop.logo_url && <img src={shop.logo_url} alt="" style={{ width: 46, height: 46, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />}
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', lineHeight: 1.1 }}>{shop.shop_name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 3 }}>New claim request — tell us about the damage</div>
        </div>
      </div>
    }>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        {/* Honeypot — hidden off-screen (not display:none), bots auto-fill it */}
        <input type="text" name="company_website" value={hp} onChange={e => setHp(e.target.value)}
          autoComplete="off" tabIndex={-1} aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />

        <Section title="Your Info">
          <div className="field">
            <label className="label">Full Name *</label>
            <input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Your full name" required />
          </div>
          <div style={{ ...pair, marginTop: 10 }}>
            <div className="field">
              <label className="label">Phone *</label>
              <input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} placeholder="(555) 555-5555" type="tel" required />
            </div>
            <div className="field">
              <label className="label">Email</label>
              <input value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder="you@email.com" type="email" />
            </div>
          </div>
        </Section>

        <Section title="Vehicle">
          <div className="field">
            <label className="label">Year / Make / Model *</label>
            <input value={form.car} onChange={e => set('car', e.target.value)} placeholder="e.g. 2021 Toyota Camry" required />
          </div>
          <div style={{ ...pair, marginTop: 10 }}>
            <div className="field">
              <label className="label">License Plate</label>
              <input value={form.plate} onChange={e => set('plate', e.target.value.toUpperCase())} placeholder="ABC1234" style={{ fontFamily: 'var(--font-mono)' }} />
            </div>
            <div className="field">
              <label className="label">Color</label>
              <input value={form.color} onChange={e => set('color', e.target.value)} placeholder="Silver" />
            </div>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label className="label">VIN <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(if you have it)</span></label>
            <input value={form.vin} onChange={e => set('vin', e.target.value.toUpperCase())} placeholder="17-digit VIN" style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
          </div>
        </Section>

        <Section title="Insurance">
          <div className="field">
            <label className="label">Insurance Company *</label>
            <input value={form.ins} onChange={e => set('ins', e.target.value)} placeholder="State Farm" list="intake-insurers" required />
            <datalist id="intake-insurers">
              {INSURERS.map(i => <option key={i.name} value={i.name} />)}
            </datalist>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label className="label">Claim Number <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(if you have one)</span></label>
            <input value={form.cnum} onChange={e => set('cnum', e.target.value)} placeholder="Claim number" style={{ fontFamily: 'var(--font-mono)' }} />
          </div>
        </Section>

        <Section title="Damage">
          <div className="field">
            <label className="label">What happened?</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5}
              placeholder="Describe the damage and how it happened..." style={{ resize: 'none' }} maxLength={3000} />
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label className="label">Photos <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional, up to {MAX_PHOTOS})</span></label>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, background: 'var(--bg3)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: 'var(--text2)' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? 's' : ''} — add more` : 'Add damage photos'}
              <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
            </label>
            {photoNote && <div style={{ fontSize: 12, color: 'var(--yellow)', marginTop: 6 }}>{photoNote}</div>}
            {photos.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {photos.map((f, i) => (
                  <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                      style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {error && <div style={{ color: 'var(--red)', fontSize: 13.5 }}>{error}</div>}

        <button type="submit" className="btn btn-primary" disabled={submitting}
          style={{ padding: 15, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>
          {submitting ? <div className="spinner" /> : 'Send Request'}
        </button>
      </form>
    </Shell>
  );
}

// Page frame with its own scroll container — body and #root are overflow:hidden.
function Shell({ header, children, scrollRef }) {
  return (
    <div ref={scrollRef} style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', background: 'var(--bg)' }}>
      <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        {header && (
          <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg2)' }}>
            <div style={{ maxWidth: 560, margin: '0 auto', padding: '22px 18px' }}>{header}</div>
          </div>
        )}
        <div style={{ flex: 1, width: '100%', maxWidth: 560, margin: '0 auto', padding: '24px 18px 40px' }}>
          {children}
        </div>
        <div style={{ padding: '18px 0 calc(18px + env(safe-area-inset-bottom))', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text3)' }}>
            Powered by Shop<span style={{ color: 'var(--orange)' }}>Track</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
